import React from 'react'
import withProps from 'recompose/withProps'
import styled, { css } from 'react-emotion'
import ReactModal from 'react-modal'
import { mediumMediaQuery, largeMediaQuery } from 'utils/style-helpers'
import Wrapper from 'components/Wrapper'
import clearIcon from './clear.svg'

const inlineModalParentId = 'inline-modal-parent'

const InlineModalParent = withProps({
  id: inlineModalParentId
})(styled.div`
  position: relative;
`)

const ModalWrapper = styled(Wrapper)`
  position: relative;
`

const CloseButton = styled.button`
  background: url(${clearIcon}) no-repeat;
  border: none;
  font-size: 0;
  height: 25px;
  margin: 0;
  opacity: 0.3;
  padding: 0;
  position: absolute;
  right: 0;
  width: 24px;
  top: 0;

  &:hover {
    cursor: pointer;
  }
`

const ModalTitle = styled.h1`
  color: #4db051;
  font-weight: 900;
  letter-spacing: 3.6px;
  text-align: center;
  text-transform: uppercase;
`

const ModalContent = styled.div`
  overflow: auto;
`

const BaseModal = ({ className, bodyOpenClassName, title, closeHandler, children, ...props }) => (
  <ReactModal
    className="Modal"
    overlayClassName="Overlay"
    portalClassName={className}
    shouldReturnFocusAfterClose={false}
    bodyOpenClassName={bodyOpenClassName}
    closeTimeoutMS={500}
    {...props}
  >
    <ModalWrapper>
      <CloseButton onClick={() => closeHandler()}>close</CloseButton>
      {title && <ModalTitle>{title}</ModalTitle>}
      <ModalContent>{children}</ModalContent>
    </ModalWrapper>
  </ReactModal>
)

const BaseModalCss = css`
  .ReactModal__Overlay,
  .ReactModal__Content {
    opacity: 0;
  }

  .ReactModal__Overlay {
    background: rgba(255, 255, 255, 0.5);
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 998;
  }

  .ReactModal__Content {
    background: #fff;
    bottom: 0;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
    display: flex;
    left: 0;
    outline: none;
    overflow: auto;
    padding: 30px 15px;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    z-index: 999;

    ${mediumMediaQuery} {
      padding: 30px;
    }
  }

  .ReactModal__Overlay--after-open,
  .ReactModal__Content--after-open {
    opacity: 1;
    transition: opacity 0.3s ease-out 0s;
  }

  .ReactModal__Overlay--before-close,
  .ReactModal__Content--before-close {
    opacity: 0;
    transition: opacity 0.5s ease-in 0s;
  }

  ${ModalWrapper} {
    display: flex;
    flex-flow: column;
    flex-grow: 1;
    max-width: none;
    overflow: auto;
    padding: 0;
  }

  ${ModalTitle} {
    flex-grow: 0;
    font-size: 24px;
    margin: 0 0 40px 0;

    ${mediumMediaQuery} {
      font-size: 32px;
    }
  }

  ${ModalContent} {
    flex-grow: 1;
  }
`

export const Dialog = withProps({
  bodyOpenClassName: 'Body--dialogOpen',
  ariaHideApp: false
})(styled(BaseModal)`
  ${BaseModalCss};

  ${mediumMediaQuery} {
    .ReactModal__Content {
      border-radius: 30px;
      bottom: auto;
      left: 50%;
      max-height: calc(100% - 80px);
      max-width: calc(100% - 80px);
      right: auto;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
`)

const StyledInlineModal = withProps({
  bodyOpenClassName: 'Body--inlineModalOpen',
  ariaHideApp: false
})(styled(BaseModal)`
  ${BaseModalCss};

  ${mediumMediaQuery} {
    .ReactModal__Overlay,
    .ReactModal__Content {
      opacity: 1;
    }

    .ReactModal__Overlay {
      position: relative;
      z-index: 0;
    }

    .ReactModal__Content {
      box-shadow: none;
      display: block;
      overflow: visible;
      position: relative;
      margin: 0 auto;
      max-height: 0;
      max-width: 1170px;
      padding: 20px;
      z-index: 0;
    }

    .ReactModal__Overlay--after-open,
    .ReactModal__Content--after-open {
      max-height: 1400px;
      transition: max-height 1s ease-out 0s;
    }

    .ReactModal__Overlay--before-close,
    .ReactModal__Content--before-close {
      max-height: 0;
      opacity: 1;
      transition: max-height 0.5s ease-in 0s;
    }

    ${ModalWrapper} {
      display: block;
      overflow: visible;
      padding: 0;
    }

    ${ModalTitle} {
      font-size: 18px;
      margin: 0 0 25px 0;
    }
  }
`)

export const InlineModal = ({ children, ...props }) => (
  <div>
    <InlineModalParent />
    <StyledInlineModal
      parentSelector={() => { return document.querySelector('#' + inlineModalParentId) }}
      {...props}>
      {children}
    </StyledInlineModal>
  </div>
)



// WEBPACK FOOTER //
// ./src/components/Modal/index.js